import { ReactElement, useEffect, useRef } from 'react';
import {
  Box,
  DialogBackdrop,
  DialogRoot,
  DialogRootProps,
  DialogPositioner,
  JsxStyleProps,
} from '@chakra-ui/react';
import { CloseIcon } from 'assets/icons/v2';
import { Z_INDEX } from 'styles/zIndex';
import { ModalTarget } from 'types/Modal';
import { createPortal } from 'react-dom';
import { CustomModalContent } from './CustomModalContent';
import { CustomModalHeader } from './CustomModalHeader';

export type ModalHeaderProps = {
  title?: string;
  handleClose: () => void;
};

type Props = Omit<DialogRootProps, 'open' | 'onOpenChange'> & {
  open?: boolean;
  isOpen?: boolean; // Support both for backward compatibility
  borderRadius?: JsxStyleProps['borderRadius'];
  bg?: JsxStyleProps['bg'];
  title?: string;
  onClose: () => void;
  hideHeader?: boolean;
  isFullscreen?: boolean;
  floatingCross?: boolean;
  target?: ModalTarget;
  renderHeaderComponent?: ({ handleClose, title }: ModalHeaderProps) => ReactElement | null;
  shouldAlwaysStretchHeight?: boolean;
};

export const CustomModal = ({
  borderRadius,
  placement = 'center',
  bg,
  title,
  onClose,
  hideHeader = false,
  floatingCross = false,
  isFullscreen,
  target = ModalTarget.APP,
  renderHeaderComponent,
  shouldAlwaysStretchHeight,
  open,
  isOpen,
  ...props
}: Props): ReactElement => {
  const calculatedBorderRadius = isFullscreen ? '0px' : borderRadius || '10px';
  const isPageTarget = target === ModalTarget.PAGE_DESKTOP || target === ModalTarget.PAGE_MOBILE;

  // Support both 'open' (v3) and 'isOpen' (backward compatibility)
  const dialogOpen = open ?? isOpen ?? false;

  const overlayZIndex = isPageTarget ? Z_INDEX.NAVBAR - 1 : Z_INDEX.MODAL_OVERLAY;

  // Get the container element for page-target modals
  const portalContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isPageTarget) {
      const container = document.getElementById(target);
      portalContainerRef.current = container;
    } else {
      portalContainerRef.current = null;
    }
  }, [target, isPageTarget]);

  const dialogContent = (
    <DialogRoot
      open={dialogOpen}
      onOpenChange={(details) => {
        if (!details.open) {
          onClose();
        }
      }}
      placement={placement}
      scrollBehavior="inside"
      closeOnInteractOutside={!isPageTarget}
      trapFocus={!isPageTarget}
      modal={!isPageTarget}
      {...props}
    >
      {/* For page-target modals, backdrop should be positioned relative to container */}
      <DialogBackdrop
        zIndex={overlayZIndex}
        backdropFilter="blur(10px)"
        bg="blackAlpha.500"
        position={isPageTarget ? 'absolute' : 'fixed'}
        inset={isPageTarget ? '0' : undefined}
      />

      {/* DialogPositioner handles positioning based on placement prop */}
      <DialogPositioner pointerEvents={isPageTarget ? 'none' : undefined}>
        <CustomModalContent
          borderRadius={calculatedBorderRadius}
          overflow="hidden"
          mx="20px"
          bg={bg}
          isFullscreen={isFullscreen}
          height={shouldAlwaysStretchHeight ? '100%' : undefined}
          zIndex={overlayZIndex + 1}
        >
          {!hideHeader &&
            (renderHeaderComponent ? (
              renderHeaderComponent({ handleClose: onClose, title })
            ) : (
              <CustomModalHeader
                title={title}
                handleClose={onClose}
                floatingCross={floatingCross}
              />
            ))}
          {floatingCross && (
            <Box position="relative" zIndex="1500">
              <CloseIcon
                w="20px"
                h="20px"
                onClick={onClose}
                cursor="pointer"
                color="text.secondary"
                position="absolute"
                right="30px"
                top="30px"
                transform="translateY(-50%)"
              />
            </Box>
          )}
          {props.children}
        </CustomModalContent>
      </DialogPositioner>
    </DialogRoot>
  );

  // Portal into the page container for page-target modals
  // This ensures the modal only renders inside the page area, not over the navbar
  if (isPageTarget && portalContainerRef.current) {
    return <>{createPortal(dialogContent, portalContainerRef.current)}</>;
  }

  return dialogContent;
};
